import React, { useEffect, useMemo, useState } from 'react';
import { addMonths, format } from 'date-fns';
import FdModal from '../FdModal';
import { useAuth } from '../../context/AuthContext';

const PAYMENT_MODES = ['Cash', 'Check', 'Bank Transfer/Deposit', 'Credit Card'];
const PAYMENT_TERMS = ['Annually', 'Bi-Annual', 'Quarterly', 'Monthly'];
const SALE_STATUSES = ['Pending', 'Confirmed', 'Cancelled'];
const SCHEDULE_COUNT = { Annually: 1, 'Bi-Annual': 2, Quarterly: 4, Monthly: 12 };
const TERMS_MONTH_GAP = { 'Bi-Annual': 6, Quarterly: 3, Monthly: 1 };
const COMMISSION_ROLES = [
  { key: 'SA', label: 'Sales Advisor' },
  { key: 'UM', label: 'Unit Manager' },
  { key: 'BM', label: 'Branch Manager' },
  { key: 'AAH', label: 'Assistant Area Head' },
  { key: 'AH', label: 'Area Head' },
  { key: 'SAH', label: 'Senior Area Head' },
  { key: 'DH', label: 'Division Head' },
  { key: 'CEO', label: 'CEO' },
];
const NON_AUTODEDUCT_ROLE_KEYS = ['AAH', 'AH', 'SAH', 'CEO'];
// Roles from Assistant Area Head up to CEO are managed at the admin level only.
// Branch users do not pick them manually; they are auto-populated from the
// branch's registered AAH / AH / SAH / DH / CEO (blank when a position is unfilled).
const ADMIN_ONLY_ROLE_KEYS = ['AAH', 'AH', 'SAH', 'DH', 'CEO'];
const ROLE_ALLOWED_POSITIONS = {
  SA: ['sales advisor'],
  UM: ['unit manager'],
  BM: ['branch manager'],
  AAH: ['assistant area head', 'assitant area head'],
  AH: ['area head'],
  SAH: ['senior area head'],
  DH: ['division head'],
  CEO: ['ceo'],
};

function normalizePosition(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'assitant area head') {
    return 'assistant area head';
  }
  return normalized;
}

function hasAgentSelection(entry) {
  return (
    String(entry?.agent_name || '').trim() !== ''
    || (
      entry?.agent_id !== null
      && entry?.agent_id !== undefined
      && String(entry.agent_id).trim() !== ''
    )
  );
}

function roleLabel(roleKey) {
  return COMMISSION_ROLES.find((role) => role.key === roleKey)?.label || roleKey;
}

// AAH / AH / SAH / DH / CEO are never chosen or pre-filled on the client. They are
// auto-filled server-side from the agents registered for those positions under the
// sale's branch (no commission when a position has no registered agent). This helper
// therefore returns no defaults; it remains so the existing call sites keep working.
function resolveCommissionRoleDefaults() {
  return {
    AAH: null,
    AH: null,
    SAH: null,
    DH: null,
    CEO: null,
  };
}

function applyBranchRoleDefaults(commissionAgents, roleDefaults = {}, options = {}) {
  const { forceBranchRoles = false } = options;

  return commissionAgents.map((entry) => {
    const defaultForRole = roleDefaults[entry.role] || null;

    if (entry.role === 'AAH' || entry.role === 'AH' || entry.role === 'SAH') {
      if (!defaultForRole) {
        return forceBranchRoles
          ? { ...entry, agent_id: '', agent_name: '' }
          : entry;
      }

      if (forceBranchRoles || !hasAgentSelection(entry)) {
        return {
          ...entry,
          agent_id: defaultForRole?.agent_id ?? '',
          agent_name: defaultForRole?.agent_name || '',
        };
      }

      return entry;
    }

    if (entry.role === 'CEO') {
      if (!defaultForRole || hasAgentSelection(entry)) {
        return entry;
      }

      return {
        ...entry,
        agent_id: defaultForRole?.agent_id ?? '',
        agent_name: defaultForRole?.agent_name || '',
      };
    }

    return entry;
  });
}

function applyCommissionRoleRules(commissionAgents, modeOfPayment) {
  const isCashMode = modeOfPayment === 'Cash';

  return commissionAgents.map((entry) => {
    const canAutodeduct = isCashMode && !NON_AUTODEDUCT_ROLE_KEYS.includes(entry.role);

    return {
      ...entry,
      autodeduct: canAutodeduct ? Boolean(entry.autodeduct) : false,
    };
  });
}

function resolveRoleAgentOptions(agents, roleKey, selectedAgentId, branchId) {
  const allowedPositions = ROLE_ALLOWED_POSITIONS[roleKey] || [];
  let options = agents.filter((agent) => allowedPositions.includes(normalizePosition(agent?.position)));

  if ((roleKey === 'AAH' || roleKey === 'AH' || roleKey === 'SAH') && String(branchId || '').trim() !== '') {
    const scoped = options.filter((agent) => Number(agent?.branch_id) === Number(branchId));
    if (scoped.length > 0) {
      options = scoped;
    }
  }

  if (selectedAgentId !== null && selectedAgentId !== undefined && String(selectedAgentId).trim() !== '') {
    const selected = agents.find((agent) => Number(agent?.id) === Number(selectedAgentId)) || null;
    if (selected && !options.some((item) => Number(item.id) === Number(selected.id))) {
      options = [selected, ...options];
    }
  }

  return options;
}

function createEmptyPlanItem() {
  return {
    plan_name: '',
    premium_amount: '',
    qty: '',
    total: 0,
  };
}

function createEmptyNaf() {
  return {
    unit_cost: '',
    qty: '',
    total: 0,
  };
}

function buildDefaultSchedules(paymentTerms, totalPremium, existing = []) {
  const count = SCHEDULE_COUNT[paymentTerms] || 0;
  if (!count) {
    return [];
  }

  const amountPerSchedule = count > 0
    ? Number((totalPremium / count).toFixed(2))
    : 0;

  return Array.from({ length: count }, (_, index) => ({
    schedule_no: index + 1,
    due_date: existing[index]?.due_date || '',
    amount_due: amountPerSchedule,
    is_paid: Boolean(existing[index]?.is_paid),
  }));
}

function parseNullableNumber(value) {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim();
  if (normalized === '') {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseNullableInteger(value) {
  const parsed = parseNullableNumber(value);
  if (parsed === null) {
    return null;
  }

  return Math.trunc(parsed);
}

function formatCurrency(value) {
  if (value === null || value === undefined) {
    return 'PHP 0.00';
  }

  return `PHP ${Number(value).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function extractProductPlans(product) {
  if (!product || !Array.isArray(product.plans)) {
    return [];
  }

  return product.plans.filter((plan) => String(plan.plan_name || '').trim() !== '');
}

function createCommissionAgents(initial = [], roleDefaults = {}) {
  return COMMISSION_ROLES.map((role) => {
    const existing = Array.isArray(initial)
      ? initial.find((item) => String(item.role || '').toUpperCase() === role.key)
      : null;
    const defaultForRole = roleDefaults[role.key] || null;
    const shouldUseDefault = defaultForRole && !hasAgentSelection(existing);

    return {
      role: role.key,
      agent_id: shouldUseDefault
        ? (defaultForRole?.agent_id ?? '')
        : (existing?.agent_id ?? ''),
      agent_name: shouldUseDefault
        ? (defaultForRole?.agent_name || '')
        : (existing?.agent_name || ''),
      autodeduct: NON_AUTODEDUCT_ROLE_KEYS.includes(role.key) ? false : Boolean(existing?.autodeduct),
    };
  });
}

export default function SaleFormModal({
  isOpen,
  onClose,
  isEditMode,
  initialSale,
  products,
  agents,
  branches,
  isSubmitting,
  error,
  onSubmit,
}) {
  const [formData, setFormData] = useState({
    date: '',
    company_client_name: '',
    contact_person: '',
    contact_position: '',
    contact_email: '',
    contact_number: '',
    branch_id: '',
    product_commission_id: '',
    product_name: '',
    mode_of_payment: '',
    payment_terms: '',
    status: 'Pending',
    notes: '',
  });
  const [planItems, setPlanItems] = useState([createEmptyPlanItem()]);
  const [naf, setNaf] = useState(createEmptyNaf());
  const [paymentSchedules, setPaymentSchedules] = useState([]);
  const [commissionAgents, setCommissionAgents] = useState(createCommissionAgents());
  const [localError, setLocalError] = useState('');
  const { isAdmin, userBranchId } = useAuth();
  const defaultBranchId = !isAdmin && userBranchId != null ? String(userBranchId) : '';

  // No one selects AAH/AH/SAH/DH/CEO on a sale — these are auto-filled server-side from
  // the registered heads of the sale's branch. Only SA/UM/BM are picked in the form.
  const visibleCommissionRoles = useMemo(
    () => COMMISSION_ROLES.filter((role) => !ADMIN_ONLY_ROLE_KEYS.includes(role.key)),
    []
  );

  const selectedProduct = useMemo(() => {
    const productId = parseNullableInteger(formData.product_commission_id);
    if (productId !== null) {
      const matchedById = products.find((product) => Number(product.id) === productId);
      if (matchedById) {
        return matchedById;
      }
    }

    return products.find((product) => product.product_name === formData.product_name) || null;
  }, [products, formData.product_commission_id, formData.product_name]);

  const productPlans = useMemo(() => extractProductPlans(selectedProduct), [selectedProduct]);

  const totalPremium = useMemo(
    () => planItems.reduce((sum, item) => sum + (parseNullableNumber(item.total) || 0), 0),
    [planItems]
  );

  const nafTotal = useMemo(
    () => {
      const unitCost = parseNullableNumber(naf.unit_cost) || 0;
      const qty = parseNullableNumber(naf.qty) || 0;
      return Number((unitCost * qty).toFixed(2));
    },
    [naf.unit_cost, naf.qty]
  );

  const grandTotal = useMemo(
    () => Number((totalPremium + nafTotal).toFixed(2)),
    [totalPremium, nafTotal]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setLocalError('');

    if (initialSale) {
      const initialBranchId = initialSale.branch_id || '';
      const roleDefaults = resolveCommissionRoleDefaults(branches, initialBranchId, agents);
      setFormData({
        date: initialSale.date || '',
        company_client_name: initialSale.company_client_name || '',
        contact_person: initialSale.contact_person || '',
        contact_position: initialSale.contact_position || '',
        contact_email: initialSale.contact_email || '',
        contact_number: initialSale.contact_number || '',
        branch_id: initialBranchId,
        product_commission_id: initialSale.product_commission_id || '',
        product_name: initialSale.product_name || '',
        mode_of_payment: initialSale.mode_of_payment || '',
        payment_terms: initialSale.payment_terms || '',
        status: initialSale.status || 'Pending',
        notes: initialSale.notes || '',
      });

      const normalizedPlanItems = Array.isArray(initialSale.plan_items) && initialSale.plan_items.length > 0
        ? initialSale.plan_items.map((item) => ({
          plan_name: item.plan_name || '',
          premium_amount: item.premium_amount ?? '',
          qty: item.qty ?? '',
          total: item.total ?? 0,
        }))
        : [createEmptyPlanItem()];

      setPlanItems(normalizedPlanItems);
      setNaf({
        unit_cost: initialSale.naf?.unit_cost ?? '',
        qty: initialSale.naf?.qty ?? '',
        total: initialSale.naf?.total ?? 0,
      });

      const normalizedSchedules = Array.isArray(initialSale.payment_schedules)
        ? initialSale.payment_schedules.map((schedule, index) => ({
          schedule_no: schedule.schedule_no || (index + 1),
          due_date: schedule.due_date || '',
          amount_due: schedule.amount_due ?? 0,
          is_paid: Boolean(schedule.is_paid),
        }))
        : [];

      setPaymentSchedules(normalizedSchedules);
      setCommissionAgents(
        applyCommissionRoleRules(
          applyBranchRoleDefaults(
            createCommissionAgents(initialSale.commission_agents, roleDefaults),
            roleDefaults
          ),
          initialSale.mode_of_payment || ''
        )
      );
    } else {
      const roleDefaults = resolveCommissionRoleDefaults(branches, '', agents);
      setFormData({
        date: new Date().toISOString().slice(0, 10),
        company_client_name: '',
        contact_person: '',
        contact_position: '',
        contact_email: '',
        contact_number: '',
        branch_id: defaultBranchId,
        product_commission_id: '',
        product_name: '',
        mode_of_payment: '',
        payment_terms: '',
        status: 'Pending',
        notes: '',
      });
      setPlanItems([createEmptyPlanItem()]);
      setNaf(createEmptyNaf());
      setPaymentSchedules([]);
      setCommissionAgents(
        applyCommissionRoleRules(
          applyBranchRoleDefaults(createCommissionAgents([], roleDefaults), roleDefaults),
          ''
        )
      );
    }
  }, [isOpen, initialSale, branches, agents, defaultBranchId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const roleDefaults = resolveCommissionRoleDefaults(branches, formData.branch_id, agents);
    setCommissionAgents((previous) => applyBranchRoleDefaults(
      previous,
      roleDefaults,
      { forceBranchRoles: true }
    ));
  }, [isOpen, formData.branch_id, branches, agents]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setCommissionAgents((previous) => applyCommissionRoleRules(previous, formData.mode_of_payment));
  }, [isOpen, formData.mode_of_payment]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!formData.payment_terms) {
      setPaymentSchedules([]);
      return;
    }

    setPaymentSchedules((previous) => buildDefaultSchedules(formData.payment_terms, totalPremium, previous));
  }, [isOpen, formData.payment_terms, totalPremium]);

  function handleFormChange(event) {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  }

  function handleProductChange(event) {
    const productId = parseNullableInteger(event.target.value);

    if (productId === null) {
      setFormData((previous) => ({
        ...previous,
        product_commission_id: '',
        product_name: '',
      }));
      setPlanItems([createEmptyPlanItem()]);
      return;
    }

    const product = products.find((item) => Number(item.id) === productId) || null;
    setFormData((previous) => ({
      ...previous,
      product_commission_id: productId,
      product_name: product?.product_name || '',
    }));

    if (!product || !Array.isArray(product.plans) || product.plans.length === 0) {
      setPlanItems([createEmptyPlanItem()]);
      return;
    }

    setPlanItems([createEmptyPlanItem()]);
  }

  function addPlanItem() {
    setPlanItems((previous) => [...previous, createEmptyPlanItem()]);
  }

  function removePlanItem(index) {
    setPlanItems((previous) => {
      if (previous.length === 1) {
        return [createEmptyPlanItem()];
      }

      return previous.filter((_, planIndex) => planIndex !== index);
    });
  }

  function updatePlanItem(index, field, value) {
    setPlanItems((previous) => previous.map((item, planIndex) => {
      if (planIndex !== index) {
        return item;
      }

      const updated = { ...item, [field]: value };

      if (field === 'plan_name') {
        const selectedPlan = productPlans.find((plan) => plan.plan_name === value) || null;
        updated.premium_amount = selectedPlan?.plan_premium ?? '';
      }

      const premium = parseNullableNumber(updated.premium_amount) || 0;
      const qty = parseNullableNumber(updated.qty) || 0;
      updated.total = Number((premium * qty).toFixed(2));

      return updated;
    }));
  }

  function updateNaf(field, value) {
    setNaf((previous) => {
      const updated = { ...previous, [field]: value };
      const unitCost = parseNullableNumber(updated.unit_cost) || 0;
      const qty = parseNullableNumber(updated.qty) || 0;
      updated.total = Number((unitCost * qty).toFixed(2));
      return updated;
    });
  }

  function updateScheduleDate(index, value) {
    setPaymentSchedules((previous) => {
      const updated = previous.map((schedule, scheduleIndex) => (
        scheduleIndex === index
          ? { ...schedule, due_date: value }
          : schedule
      ));

      if (index === 0 && value && TERMS_MONTH_GAP[formData.payment_terms]) {
        const gap = TERMS_MONTH_GAP[formData.payment_terms];
        const baseDate = new Date(`${value}T00:00:00`);

        return updated.map((schedule, scheduleIndex) => (
          scheduleIndex === 0
            ? schedule
            : { ...schedule, due_date: format(addMonths(baseDate, scheduleIndex * gap), 'yyyy-MM-dd') }
        ));
      }

      return updated;
    });
  }

  function updateCommissionAgent(role, field, value) {
    setCommissionAgents((previous) => previous.map((entry) => {
      if (entry.role !== role) {
        return entry;
      }

      if (field === 'agent_id') {
        const normalizedAgentId = parseNullableInteger(value);
        const matchedAgent = normalizedAgentId !== null
          ? agents.find((agent) => Number(agent.id) === normalizedAgentId)
          : null;

        return {
          ...entry,
          agent_id: normalizedAgentId ?? '',
          agent_name: matchedAgent?.full_name || '',
          autodeduct: NON_AUTODEDUCT_ROLE_KEYS.includes(entry.role) ? false : Boolean(entry.autodeduct),
        };
      }

      if (field === 'autodeduct' && NON_AUTODEDUCT_ROLE_KEYS.includes(entry.role)) {
        return { ...entry, autodeduct: false };
      }

      return { ...entry, [field]: value };
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setLocalError('');

    if (!String(formData.company_client_name || '').trim()) {
      setLocalError('Company / client name is required.');
      return;
    }

    if (!String(formData.product_name || '').trim()) {
      setLocalError('Product is required.');
      return;
    }

    const normalizedPlanItems = planItems
      .map((item) => {
        const planName = String(item.plan_name || '').trim();
        if (!planName) {
          return null;
        }

        const premium = parseNullableNumber(item.premium_amount) || 0;
        const qty = parseNullableInteger(item.qty) || 0;
        const total = Number((premium * qty).toFixed(2));

        return {
          plan_name: planName,
          premium_amount: premium,
          qty,
          total,
        };
      })
      .filter(Boolean);

    if (normalizedPlanItems.length === 0) {
      setLocalError('At least one plan item is required.');
      return;
    }

    for (const item of commissionAgents) {
      const agentId = parseNullableInteger(item.agent_id);
      if (agentId === null) {
        continue;
      }

      const selectedAgent = agents.find((agent) => Number(agent.id) === agentId) || null;
      const allowedPositions = ROLE_ALLOWED_POSITIONS[item.role] || [];
      if (!selectedAgent || !allowedPositions.includes(normalizePosition(selectedAgent.position))) {
        setLocalError(`Selected agent for ${roleLabel(item.role)} is invalid. Please select a valid ${roleLabel(item.role)}.`);
        return;
      }
    }

    const normalizedCommissionAgents = commissionAgents
      .map((item) => {
        const agentId = parseNullableInteger(item.agent_id);
        const agentName = String(item.agent_name || '').trim() || null;
        if (agentId === null && agentName === null) {
          return null;
        }
        const autodeductAllowed = formData.mode_of_payment === 'Cash'
          && !NON_AUTODEDUCT_ROLE_KEYS.includes(item.role);

        return {
          role: item.role,
          agent_id: agentId,
          agent_name: agentName,
          autodeduct: autodeductAllowed ? Boolean(item.autodeduct) : false,
        };
      })
      .filter(Boolean);

    const saEntry = normalizedCommissionAgents.find((item) => item.role === 'SA') || null;

    const payload = {
      date: formData.date || null,
      company_client_name: String(formData.company_client_name || '').trim(),
      contact_person: String(formData.contact_person || '').trim() || null,
      contact_position: String(formData.contact_position || '').trim() || null,
      contact_email: String(formData.contact_email || '').trim() || null,
      contact_number: String(formData.contact_number || '').trim() || null,
      branch_id: parseNullableInteger(formData.branch_id),
      branch: branches.find((branch) => Number(branch.id) === Number(formData.branch_id))?.name || null,
      product_commission_id: parseNullableInteger(formData.product_commission_id),
      product_name: String(formData.product_name || '').trim(),
      agent_id: saEntry?.agent_id ?? null,
      agent_name: saEntry?.agent_name ?? null,
      plan_items: normalizedPlanItems,
      naf: {
        unit_cost: parseNullableNumber(naf.unit_cost) || 0,
        qty: parseNullableInteger(naf.qty) || 0,
        total: nafTotal,
      },
      premium_amount: totalPremium,
      total_amount: grandTotal,
      mode_of_payment: formData.mode_of_payment || null,
      payment_terms: formData.payment_terms || null,
      payment_schedules: paymentSchedules.map((schedule, index) => ({
        schedule_no: schedule.schedule_no || (index + 1),
        due_date: schedule.due_date || null,
        amount_due: parseNullableNumber(schedule.amount_due) || 0,
        is_paid: Boolean(schedule.is_paid),
      })),
      commission_agents: normalizedCommissionAgents,
      status: formData.status || 'Pending',
      notes: String(formData.notes || '').trim() || null,
    };

    await onSubmit(payload);
  }

  return (
    <FdModal
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Sales"
      title={isEditMode ? 'Edit Sale' : 'Record New Sale'}
      subtitle="Capture sales details, plans, payment schedules, and commission agents."
      wide
      footer={(
        <>
          <button type="button" className="fd-btn fd-btn-cancel" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" form="sale-form" className="fd-btn fd-btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Sale' : 'Record Sale')}
          </button>
        </>
      )}
    >
      <form id="sale-form" onSubmit={handleSubmit}>
        <p className="fd-section-title">Sale Header</p>
        <div className="fd-modal-grid">
          <div className="fd-field">
            <label htmlFor="date">Sale Date</label>
            <input id="date" type="date" name="date" value={formData.date} onChange={handleFormChange} />
          </div>
          <div className="fd-field">
            <label htmlFor="status">Status</label>
            <select id="status" name="status" value={formData.status} onChange={handleFormChange}>
              {SALE_STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div className="fd-field fd-field-wide">
            <label htmlFor="company_client_name">Company / Client Name *</label>
            <input
              id="company_client_name"
              type="text"
              name="company_client_name"
              value={formData.company_client_name}
              onChange={handleFormChange}
              placeholder="Enter client or company name"
              required
            />
          </div>
        </div>

        <p className="fd-section-title">Contact Details</p>
        <div className="fd-modal-grid">
          <div className="fd-field">
            <label htmlFor="contact_person">Contact Person</label>
            <input id="contact_person" type="text" name="contact_person" value={formData.contact_person} onChange={handleFormChange} />
          </div>
          <div className="fd-field">
            <label htmlFor="contact_position">Position</label>
            <input id="contact_position" type="text" name="contact_position" value={formData.contact_position} onChange={handleFormChange} />
          </div>
          <div className="fd-field">
            <label htmlFor="contact_email">Email Address</label>
            <input id="contact_email" type="email" name="contact_email" value={formData.contact_email} onChange={handleFormChange} />
          </div>
          <div className="fd-field">
            <label htmlFor="contact_number">Contact Number</label>
            <input id="contact_number" type="text" name="contact_number" value={formData.contact_number} onChange={handleFormChange} />
          </div>
        </div>

        <p className="fd-section-title">Product & Branch</p>
        <div className="fd-modal-grid">
          <div className="fd-field">
            <label htmlFor="branch_id">Branch</label>
            <select
              id="branch_id"
              name="branch_id"
              value={formData.branch_id}
              onChange={handleFormChange}
              disabled={!isAdmin}
            >
              <option value="">Select branch</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </select>
          </div>
          <div className="fd-field">
            <label htmlFor="product_commission_id">Product *</label>
            <select
              id="product_commission_id"
              name="product_commission_id"
              value={formData.product_commission_id}
              onChange={handleProductChange}
              required
            >
              <option value="">Select product</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.product_name}
                  {product.is_active === false ? ' (Inactive)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="fd-section-title">Plans & Premiums</p>
        <div className="fd-modal-grid">
          {planItems.map((item, index) => (
            <div className="fd-field fd-field-wide" key={`plan-item-${index}`}>
              <div className="sale-plan-row">
                <select
                  value={item.plan_name}
                  onChange={(event) => updatePlanItem(index, 'plan_name', event.target.value)}
                >
                  <option value="">Select plan</option>
                  {productPlans.map((plan) => (
                    <option key={`${plan.plan_name}-${index}`} value={plan.plan_name}>
                      {plan.plan_name}
                    </option>
                  ))}
                </select>
                <input type="number" value={item.premium_amount} readOnly placeholder="Premium" />
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={item.qty}
                  onChange={(event) => updatePlanItem(index, 'qty', event.target.value)}
                  placeholder="Qty"
                />
                <input type="number" value={item.total} readOnly placeholder="Total" />
                <button
                  type="button"
                  className="profile-danger-btn profile-danger-btn-xs"
                  onClick={() => removePlanItem(index)}
                  disabled={planItems.length === 1}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <div className="fd-field fd-field-wide sale-plan-controls">
            <button type="button" className="profile-secondary-btn profile-secondary-btn-sm" onClick={addPlanItem}>
              + Add Plan
            </button>
            <div className="sale-total-tag">Total Premium: {formatCurrency(totalPremium)}</div>
          </div>
        </div>

        <p className="fd-section-title">Network Access Fee (NAF)</p>
        <div className="fd-modal-grid">
          <div className="fd-field">
            <label htmlFor="naf_unit_cost">Unit Cost</label>
            <input id="naf_unit_cost" type="number" min="0" step="0.01" value={naf.unit_cost} onChange={(event) => updateNaf('unit_cost', event.target.value)} />
          </div>
          <div className="fd-field">
            <label htmlFor="naf_qty">Quantity</label>
            <input id="naf_qty" type="number" min="0" step="1" value={naf.qty} onChange={(event) => updateNaf('qty', event.target.value)} />
          </div>
          <div className="fd-field">
            <label htmlFor="naf_total">Total</label>
            <input id="naf_total" type="number" value={nafTotal} readOnly />
          </div>
          <div className="fd-field fd-field-wide">
            <div className="sale-grand-total-card">
              <span>Grand Total (Premium + NAF)</span>
              <strong>{formatCurrency(grandTotal)}</strong>
            </div>
          </div>
        </div>

        <p className="fd-section-title">Payment Details</p>
        <div className="fd-modal-grid">
          <div className="fd-field">
            <label htmlFor="mode_of_payment">Mode of Payment</label>
            <select id="mode_of_payment" name="mode_of_payment" value={formData.mode_of_payment} onChange={handleFormChange}>
              <option value="">Select mode</option>
              {PAYMENT_MODES.map((mode) => (
                <option key={mode} value={mode}>{mode}</option>
              ))}
            </select>
          </div>
          <div className="fd-field">
            <label htmlFor="payment_terms">Payment Terms</label>
            <select id="payment_terms" name="payment_terms" value={formData.payment_terms} onChange={handleFormChange}>
              <option value="">Select terms</option>
              {PAYMENT_TERMS.map((terms) => (
                <option key={terms} value={terms}>{terms}</option>
              ))}
            </select>
          </div>
        </div>

        {paymentSchedules.length > 0 ? (
          <>
            <p className="fd-section-title">Payment Schedules (Premium Only)</p>
            <div className="fd-modal-grid">
              {paymentSchedules.map((schedule, index) => (
                <div key={`schedule-${index}`} className="fd-field fd-field-wide">
                  <div className="sale-schedule-row">
                    <span className="sale-schedule-label">Schedule {schedule.schedule_no}</span>
                    <input
                      type="date"
                      value={schedule.due_date}
                      onChange={(event) => updateScheduleDate(index, event.target.value)}
                    />
                    <input type="number" value={schedule.amount_due} readOnly />
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        <p className="fd-section-title">Commission Agents</p>
        {formData.mode_of_payment && formData.mode_of_payment !== 'Cash' ? (
          <p className="sale-inline-note">Auto-deduct is enabled only for Cash mode of payment.</p>
        ) : null}
        <div className="fd-modal-grid">
          {visibleCommissionRoles.map((role) => {
            const entry = commissionAgents.find((item) => item.role === role.key) || null;
            const roleAgentOptions = resolveRoleAgentOptions(
              agents,
              role.key,
              entry?.agent_id ?? '',
              formData.branch_id
            );
            const isAutoDeductDisabledRole = NON_AUTODEDUCT_ROLE_KEYS.includes(role.key);

            return (
              <div className="fd-field fd-field-wide" key={role.key}>
                <div className="sale-commission-row">
                  <span className="sale-commission-role">{role.key}</span>
                  <select
                    value={entry?.agent_id || ''}
                    onChange={(event) => updateCommissionAgent(role.key, 'agent_id', event.target.value)}
                  >
                    <option value="">Select {role.label}</option>
                    {roleAgentOptions.map((agent) => (
                      <option key={`${role.key}-${agent.id}`} value={agent.id}>
                        {agent.full_name}
                      </option>
                    ))}
                  </select>
                  <label className="sale-checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(entry?.autodeduct)}
                      onChange={(event) => updateCommissionAgent(role.key, 'autodeduct', event.target.checked)}
                      disabled={
                        !entry?.agent_id
                        || formData.mode_of_payment !== 'Cash'
                        || isAutoDeductDisabledRole
                      }
                    />
                    {isAutoDeductDisabledRole ? 'Auto-deduct (N/A)' : 'Auto-deduct'}
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        <p className="fd-section-title">Notes</p>
        <div className="fd-modal-grid">
          <div className="fd-field fd-field-wide">
            <label htmlFor="notes">Notes (optional)</label>
            <input id="notes" type="text" name="notes" value={formData.notes} onChange={handleFormChange} />
          </div>
        </div>

        {(localError || error) && (
          <div className="error-box" style={{ marginTop: 12 }}>
            {localError || error}
          </div>
        )}
      </form>
    </FdModal>
  );
}
